import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAxiosSwagger } from '../../hooks/useAxiosSwagger';
import { useEnvironmentWatches, useRolloutWatch } from '../../hooks/useRollout';
import { ReadinessState, RolloutCounts, RolloutEvent, RolloutEventKind, startRolloutWatch, stopRolloutWatch } from '../../api/rollout';
import { ago, clock, duration, number, shortBuild, splitBuilds, when } from '../data/format';
import { apiError } from '../data/usePoll';
import { Card, Empty, ErrorNotice, Freshness, Kv, Lamp, Loading, Spinner } from '../components/ui';
import { useBarTip } from '../components/BarTip';
import { v2 } from '../paths';

const WATCH_STATUS: Record<string, string> = { watching: 'loopt', done: 'klaar', stopped: 'gestopt' };

const LAMP: Record<ReadinessState, 'ok' | 'warn' | 'bad' | 'off'> = { Ok: 'ok', Warn: 'warn', Bad: 'bad', Unknown: 'off' };

const TILES: { key: keyof RolloutCounts; label: string; tone?: string }[] = [
    { key: 'offered', label: 'Aangeboden' },
    { key: 'succeeded', label: 'Geslaagd', tone: 'ok' },
    { key: 'failed', label: 'Mislukt', tone: 'warn' },
    { key: 'open', label: 'Nog open' },
];

const EVENT: Record<RolloutEventKind, { label: string; tone: 'ok' | 'warn' | 'bad' | 'off'; apps?: boolean }> = {
    Started: { label: 'App gestart', tone: 'ok', apps: true },
    Restarted: { label: 'App herstart', tone: 'warn', apps: true },
    Stopped: { label: 'App gestopt', tone: 'bad', apps: true },
    Silent: { label: 'Geen hartslag', tone: 'bad', apps: true },
    Deploy: { label: 'Deploy gezien', tone: 'ok' },
    Problem: { label: 'Nieuwe probleemgroep', tone: 'warn' },
    Telemetry: { label: 'Fout in App Insights', tone: 'warn' },
    Light: { label: 'Lampje op oranje of rood', tone: 'warn' },
    LightCleared: { label: 'Lampje weer groen', tone: 'ok' },
};

const APPS = ['cbrcv', 'cbsnd', 'exrcv', 'exsnd'];
const appOf = (subject: string) => APPS.find((a) => subject.toLowerCase().endsWith(a)) ?? subject;

/**
 * The events as rows that say what happened to what: the app by its short name, and the same kind of app event within
 * a few minutes as one row ("App gestart: cbrcv cbsnd exrcv exsnd"), not five times "gestart".
 */
export const eventRows = (events: RolloutEvent[]) => {
    const rows: { at: string; kind: RolloutEventKind; label: string; tone: 'ok' | 'warn' | 'bad' | 'off'; subjects: string[]; text: string; full: string }[] = [];
    events.forEach((e) => {
        const look = EVENT[e.kind] ?? { label: e.text, tone: 'off' as const };
        const prev = rows[rows.length - 1];
        const near = prev && prev.kind === e.kind && look.apps && Math.abs(new Date(prev.at).getTime() - new Date(e.at).getTime()) < 5 * 60000;
        const subject = look.apps && e.subject ? appOf(e.subject) : '';
        // The event's own text, unless it only repeats the kind ("gestart").
        const text = look.apps || e.text.trim().toLowerCase() === look.label.toLowerCase() ? '' : e.text;
        if (near) {
            if (subject && !prev.subjects.includes(subject)) prev.subjects.push(subject);
            prev.full += `\n${e.subject} ${e.text}`;
            return;
        }
        rows.push({ at: e.at, kind: e.kind, label: look.label, tone: look.tone, subjects: subject ? [subject] : [], text, full: `${e.subject} ${e.text}` });
    });
    rows.forEach((r) => {
        if (r.subjects.length > 1) r.label = `${r.subjects.length} apps ${r.label.replace(/^App /, '').toLowerCase()}`;
    });
    return rows;
};

/** Before and after, per app: one line when every app runs the same build, a small table when not. */
const Builds = ({ after, before }: { after?: string | null; before?: string | null }) => {
    const now = splitBuilds(after);
    const prev = splitBuilds(before);
    const uniform = (list: { build: string }[]) => list.length > 0 && list.every((b) => b.build === list[0].build);
    const apps = (list: unknown[]) => (list.length > 1 ? ` · ${list.length} apps` : '');

    if ((now.length === 0 || uniform(now)) && (prev.length === 0 || uniform(prev))) {
        return (
            <Kv
                rows={[
                    ['Build', <span className="mono">{now[0]?.build ?? '–'}<span className="mut">{apps(now)}</span></span>],
                    ['Vorige', <span className="mono">{prev[0]?.build ?? '–'}<span className="mut">{apps(prev)}</span></span>],
                ]}
            />
        );
    }

    const names = Array.from(new Set([...now, ...prev].map((b) => b.app)));
    return (
        <table className="tbl" style={{ fontSize: 12 }}>
            <thead>
                <tr><th>App</th><th>Nu</th><th>Vorige</th></tr>
            </thead>
            <tbody>
                {names.map((app) => {
                    const a = now.find((b) => b.app === app)?.build;
                    const b = prev.find((x) => x.app === app)?.build;
                    return (
                        <tr key={app}>
                            <td className="mono">{app || '–'}</td>
                            <td className={`mono ${a && b && a !== b ? 'info' : ''}`}>{a ?? '–'}</td>
                            <td className="mono mut">{b ?? '–'}</td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
};

/** At most this many columns: wider than the card they would run off it. */
const MAX_SLOTS = 120;

/**
 * The minutes of a watch as columns: a minute per column, or a few minutes together on a long watch, so the chart
 * always fits its card. Each column knows what got through, what failed and whether it lies before the deploy.
 */
export const bucketMinutes = (minutes: { minute: string; counts: Record<string, number | undefined> }[], deployedAt: string) => {
    const size = Math.max(1, Math.ceil(minutes.length / MAX_SLOTS));
    const deployed = new Date(deployedAt).getTime();
    const slots: { key: string; label: string; ok: number; failed: number; before: boolean }[] = [];
    for (let i = 0; i < minutes.length; i += size) {
        const part = minutes.slice(i, i + size);
        const from = part[0].minute;
        const to = part[part.length - 1].minute;
        slots.push({
            key: from,
            label: size > 1 ? `${clock(from)}–${clock(new Date(new Date(to).getTime() + 60000).toISOString())}` : clock(from),
            ok: part.reduce((n, m) => n + (m.counts.Succeeded ?? 0), 0),
            failed: part.reduce((n, m) => n + (m.counts.Failed ?? 0), 0),
            before: new Date(to).getTime() < deployed,
        });
    }
    const max = Math.max(1, ...slots.map((s) => s.ok + s.failed));
    const deployIndex = slots.findIndex((s) => !s.before);
    return { slots, max, size, deployIndex };
};

export const MonitorPage = () => {
    const { customer = '', environment = '' } = useParams();
    const axios = useAxiosSwagger();
    const { watches, reload } = useEnvironmentWatches(customer, environment);
    const [chosen, setChosen] = useState<string>();
    const latest = watches?.[0];
    const watchId = chosen ?? latest?.rowKey;
    const { view, live } = useRolloutWatch(watchId);
    const [busy, setBusy] = useState(false);
    const [failed, setFailed] = useState<string>();

    const bars = useMemo(() => (view ? bucketMinutes(view.timeline ?? [], view.deployedAt) : bucketMinutes([], new Date().toISOString())), [view]);
    const tip = useBarTip();

    const start = async () => {
        if (!axios) return;
        setBusy(true);
        try {
            await startRolloutWatch(axios, customer, environment);
            setFailed(undefined);
            reload();
        } catch (e) {
            setFailed(`Starten lukte niet: ${apiError(e)}`);
        } finally {
            setBusy(false);
        }
    };

    const stop = async () => {
        if (!axios || !view) return;
        setBusy(true);
        try {
            await stopRolloutWatch(axios, view.id);
            setFailed(undefined);
            reload();
        } catch (e) {
            setFailed(`Stoppen lukte niet: ${apiError(e)}`);
        } finally {
            setBusy(false);
        }
    };

    // Up to now, or up to the end of the watch once it is over: an old watch does not keep counting.
    const elapsedMin = view ? Math.max(0, Math.floor((Math.min(Date.now(), new Date(view.endsAt).getTime()) - new Date(view.deployedAt).getTime()) / 60000)) : 0;
    const totalMin = view ? Math.max(1, (new Date(view.endsAt).getTime() - new Date(view.deployedAt).getTime()) / 60000) : 1;

    return (
        <>
            <div className="toolbar">
                <div className="hrow" style={{ gap: 8 }}>
                    <span className="h2">{view?.kind === 'monitor' ? 'Monitor' : view ? 'Na de deploy' : 'Monitor'}</span>
                    {view?.status === 'watching' && (
                        <span className="chip info">
                            <Spinner />
                            loopt
                        </span>
                    )}
                    {view && view.status !== 'watching' && <span className="chip">{view.status === 'done' ? 'klaar' : 'gestopt'}</span>}
                </div>
                <div className="hrow">
                    {(watches ?? []).length > 1 && (
                        <select className="search" value={watchId} onChange={(e) => setChosen(e.target.value)} aria-label="Kies een wacht">
                            {(watches ?? []).map((w) => (
                                <option key={w.rowKey} value={w.rowKey}>
                                    {when(w.deployedAt)} · {w.kind === 'monitor' ? 'monitor' : shortBuild(w.buildAfter)} · {WATCH_STATUS[w.status] ?? w.status}
                                </option>
                            ))}
                        </select>
                    )}
                    {view?.kind !== 'monitor' && <Link className="btn" to={`${v2.envIssues(customer, environment)}?tab=deploy`}>Herbeoordelen</Link>}
                    {view?.status === 'watching' ? (
                        <button className="btn ghost" disabled={busy} onClick={stop}>Stoppen</button>
                    ) : (
                        <button className="btn" disabled={busy} onClick={start}>Monitor starten</button>
                    )}
                </div>
            </div>

            {failed && <ErrorNotice error={failed} />}
            {!watches && <Loading>Monitors van deze omgeving ophalen…</Loading>}
            {watches && watches.length === 0 && <Empty>Nog geen monitor voor deze omgeving. Start er een, of wacht op de volgende deploy.</Empty>}
            {watchId && !view && <Loading />}

            {view && (
                <>
                    <Card>
                        <div className="cb g3" style={{ gap: 24 }}>
                            <Builds after={view.buildAfter} before={view.buildBefore} />
                            <Kv rows={[[view.kind === 'monitor' ? 'Gestart' : 'Deploy gezien', when(view.deployedAt)], ['Bewaakt tot', when(view.endsAt)]]} />
                            <Kv rows={[[view.kind === 'monitor' ? 'Loopt' : 'Na deploy', duration(elapsedMin * 60)], ['Gelezen', view.readAt ? `${ago(view.readAt)} geleden` : '–']]} />
                        </div>
                        <div style={{ padding: '0 16px 14px' }}>
                            <div style={{ position: 'relative', height: 10, borderRadius: 5, background: '#1C2430', overflow: 'hidden' }}>
                                <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${Math.min(100, (elapsedMin / totalMin) * 100)}%`, background: '#3E86B5' }} />
                            </div>
                        </div>
                    </Card>

                    <div className="lights">
                        {view.lights.map((l) => (
                            <div key={l.key} className="card light">
                                <Lamp tone={LAMP[l.state]} />
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ fontWeight: 600 }}>{l.label}</div>
                                    <div className="mut" style={{ fontSize: 13, marginTop: 4 }}>{l.detail}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="tiles">
                        {TILES.map((t) => (
                            <div key={t.key} className="card tile">
                                <span className="l">{t.label}</span>
                                <span className={`v ${t.tone ?? ''}`}>{number(view.afterObjects?.[t.key])}</span>
                                <span className="s">24u vóór: <span className="mono">{number(view.beforeObjects?.[t.key])}</span></span>
                            </div>
                        ))}
                        <div className="card tile">
                            <span className="l">Vanzelf hersteld</span>
                            <span className="v info">{number(view.healed)}</span>
                            <span className="s">na de deploy</span>
                        </div>
                    </div>

                    <Card
                        title={bars.size > 1 ? `Per ${bars.size} minuten` : 'Per minuut'}
                        right={
                            <span className="hrow mut" style={{ gap: 14, fontSize: 12 }}>
                                <span><span style={{ color: '#2F9E6E' }}>■</span> gelukt</span>
                                <span><span style={{ color: '#FF6B78' }}>■</span> mislukt</span>
                                <span><span style={{ color: '#24584A' }}>■</span> vóór de deploy</span>
                            </span>
                        }
                    >
                        <div className="tiphost" ref={tip.host}>
                            <div className="bars">
                                {bars.slots.map((b) => (
                                    <span
                                        key={b.key}
                                        className={`col ${b.before ? 'before' : ''}`}
                                        {...tip.bind({
                                            title: b.label,
                                            rows: [
                                                { label: 'Gelukt', value: number(b.ok), color: '#2F9E6E' },
                                                { label: 'Mislukt', value: number(b.failed), color: '#FF6B78' },
                                            ],
                                            note: b.before ? 'vóór de deploy' : undefined,
                                        })}
                                    >
                                        {b.ok + b.failed === 0 ? (
                                            <i className="empty" />
                                        ) : (
                                            <>
                                                <i className="ok" style={{ height: `${(b.ok / bars.max) * 100}%` }} />
                                                <i className="f" style={{ height: `${(b.failed / bars.max) * 100}%` }} />
                                            </>
                                        )}
                                    </span>
                                ))}
                                {bars.deployIndex > 0 && (
                                    <div className="vline" style={{ left: `${(bars.deployIndex / Math.max(1, bars.slots.length)) * 100}%` }}>
                                        <span>{view.kind === 'monitor' ? 'start' : 'deploy'} {clock(view.deployedAt)}</span>
                                    </div>
                                )}
                            </div>
                            {tip.layer}
                        </div>
                    </Card>

                    <div className="g2">
                        <Card title="Probleemgroepen" right={<span className="mut" style={{ fontSize: 12 }}>nieuw = faalde niet vóór de deploy</span>}>
                            {view.problems.length === 0 && <Empty>Geen mislukkingen na de deploy.</Empty>}
                            <div className="tw">
                                <table className="tbl">
                                    <thead>
                                        <tr><th>Groep</th><th>Herkomst</th><th className="num">Objecten</th><th className="num">Hersteld</th></tr>
                                    </thead>
                                    <tbody>
                                        {view.problems.map((p) => (
                                            <tr key={p.code} className="hov">
                                                <td>
                                                    <Link to={`${v2.envIssues(customer, environment)}?code=${encodeURIComponent(p.code)}`}><b>{p.title}</b></Link>
                                                    {p.waitingOn && p.waitingOn.length > 0 && <div className="mut" style={{ fontSize: 12 }}>wacht op {p.waitingOn.slice(0, 2).join(', ')}</div>}
                                                </td>
                                                <td>{p.isNew ? <span className="chip warn">nieuw</span> : (p.backlog ?? 0) > 0 ? <span className="chip">oud</span> : <span className="chip info">bekend</span>}</td>
                                                <td className="num">{p.objects}</td>
                                                <td className="num">{p.healed}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                        <Card title="Gebeurtenissen" right={<span className="mut" style={{ fontSize: 12 }}>{live ? 'live' : 'verbinden…'}</span>}>
                            {view.events.length === 0 && <Empty>Nog niets gebeurd.</Empty>}
                            {eventRows(view.events).slice(0, 12).map((e, n) => (
                                <div key={n} className="ev" style={{ gridTemplateColumns: '54px 14px minmax(0,1fr)' }}>
                                    <span className="mono mut" style={{ fontSize: 12 }}>{clock(e.at)}</span>
                                    <Lamp tone={e.tone} size={8} />
                                    <span className="w" title={e.full}>
                                        <b style={{ fontWeight: 500 }}>{e.label}</b>
                                        {e.subjects.length > 0 && (
                                            <span className="hrow" style={{ display: 'inline-flex', gap: 4, marginLeft: 8 }}>
                                                {e.subjects.map((s) => (
                                                    <span key={s} className="apptag">{s}</span>
                                                ))}
                                            </span>
                                        )}
                                        {e.text && <span className="mut" style={{ marginLeft: 8 }}>{e.text}</span>}
                                    </span>
                                </div>
                            ))}
                            <Freshness
                                items={[
                                    { label: 'Tabellen', at: view.freshness.tables, staleAfter: Math.max(30, view.freshness.tablesEvery * 3) },
                                    { label: 'Apps', at: view.freshness.apps, staleAfter: Math.max(30, view.freshness.appsEvery * 3) },
                                    { label: 'App Insights', at: view.freshness.telemetry, staleAfter: Math.max(120, view.freshness.telemetryEvery * 3) },
                                    { label: 'Webhooks', at: view.freshness.webhooks, staleAfter: Math.max(300, view.freshness.webhooksEvery * 3) },
                                ]}
                            />
                        </Card>
                    </div>
                </>
            )}
        </>
    );
};
